import { useState, useMemo } from 'react';
import { apiFetch } from '../../../../utils/api';
import { sortByName } from '../../../../utils/sortingUtils';
import TreeNode from './TreeNode';

/**
 * FeaturesTree - Tree view of all features for a given context
 */
export default function FeaturesTree({ features, currentContext, onRefresh, onRuleCellsChanged, onSearchFeature, onSearchValue }) {
    const [newVarName, setNewVarName] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    const handleAddFeature = async (e) => {
        e.preventDefault();
        if (!newVarName.trim()) return;
        setIsAdding(true);
        try {
            const res = await apiFetch('/api/features', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Name: newVarName.trim(),
                    IsGlobal: false,
                    FamilyId: currentContext.id === 'global' ? null : currentContext.id
                })
            });
            if (res.ok) {
                setNewVarName('');
                onRefresh();
            } else {
                const text = await res.text();
                alert(`Failed to add feature: ${text}`);
            }
        } catch (err) {
            console.error(err);
            alert("Failed to connect to the Rule Engine server.");
        } finally {
            setIsAdding(false);
        }
    };

    const filteredFeatures = useMemo(() => {
        const list = (!currentContext || currentContext.id === 'global')
            ? features.filter(v => v.isGlobal || !v.sourceFamilyIds || v.sourceFamilyIds.length === 0)
            : features.filter(v => v.isGlobal || (v.sourceFamilyIds && v.sourceFamilyIds.includes(currentContext.id)));
        return sortByName(list, 'name');
    }, [features, currentContext]);

    const tree = useMemo(() => {
        const root = { name: 'Root', children: {}, features: [] };
        filteredFeatures.forEach(v => {
            const parts = v.name.split('.');
            let current = root;
            parts.forEach((part, index) => {
                if (index === parts.length - 1) {
                    if (!current.features) current.features = [];
                    current.features.push({
                        name: part,
                        fullName: v.name,
                        values: v.values || [],
                        rawValues: v.rawValues || v.values || [],
                        isGlobal: v.isGlobal,
                        id: v.id,
                        raw: v
                    });
                } else {
                    if (!current.children[part]) {
                        current.children[part] = { name: part, children: {}, features: [] };
                    }
                    current = current.children[part];
                }
            });
        });
        return root;
    }, [filteredFeatures]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ padding: '1rem', overflowY: 'auto', flex: 1, fontSize: '0.9rem' }}>
                {filteredFeatures.length === 0 ? (
                    <div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: '2rem', marginBottom: '2rem', fontSize: '0.85rem' }}>
                        No features used in this context
                    </div>
                ) : (
                    <TreeNode node={tree} path="" isRoot onRefresh={onRefresh} onRuleCellsChanged={onRuleCellsChanged} onSearchFeature={onSearchFeature} onSearchValue={onSearchValue} familyId={currentContext?.id} />
                )}

                <form onSubmit={handleAddFeature} style={{ display: 'flex', gap: '0.25rem', marginTop: '0.75rem', marginLeft: '1rem' }}>
                    <input 
                        className="input-field" 
                        placeholder="Add feature..." 
                        value={newVarName} 
                        onChange={e => setNewVarName(e.target.value)}
                        style={{ fontSize: '0.8rem', padding: '2px 4px', width: '120px' }}
                    />
                    <button 
                        type="submit" 
                        className="btn-secondary" 
                        disabled={isAdding || !newVarName.trim()}
                        style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                    >
                        {isAdding ? 'Adding...' : 'Add'}
                    </button>
                </form>
            </div>
        </div>
    );
}
